import * as React from 'react';

import _ from 'underscore';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

type ChildProps = {
    onChange: (arg1: any) => void;
    isSaving: boolean;
    hasChanged: boolean;
    errorMessage: string | null | undefined;
};

type Props = {
    onSave: (arg1: any) => Promise<any>;
    children: (
        arg1: ChildProps
    ) => React.ReactElement<React.ComponentProps<React.ComponentType<ChildProps>>>;
};

export function SavingDebounce(props: Props) {
    const [hasChanged, setHasChanged] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    // This is used for if the user performs a change while we're still doing a
    // previous save. The data that would be saved will be "cached" and when the
    // current save completes, we will save this data.
    const [nextSavingData, setNextSavingData] = React.useState<any | null | undefined>(null);

    const save = (saveData: any) => {
        if (isSaving) {
            // If we're currently saving, keep the data in state. This will be
            // saved as soon as the current save call finishes.
            setNextSavingData(saveData);

            return;
        }

        setIsSaving(true);
        setHasChanged(true);
        props
            .onSave(saveData)
            .then(() => {
                setIsSaving(false);
                setHasChanged(false);
                // Check to see if theres new data to save. If so, call this
                // function again. We should maybe find a way to do this without
                // risk of a stack overflow, but that seems unlikely.
                if (nextSavingData) {
                    const nextData = nextSavingData;
                    setNextSavingData(null);
                    save(nextData);
                }
            })
            .catch((error) => {
                setIsSaving(false);
                setHasChanged(false);
                setErrorMessage(safelyGetGraphQLErrorMessage(error));
            });
    };

    return props.children({
        onChange: (saveData) => {
            setHasChanged(true);
            // Debounce calling save to once a second
            // @ts-expect-error
            _.debounce((dataToSave) => {
                save(dataToSave);
            }, 1000)(saveData);
        },
        isSaving: isSaving,
        hasChanged: hasChanged,
        errorMessage: errorMessage,
    });
}
